/**
 * Probability Disclosure 렌더 util — Plan 08-03 Task 2 (GAME-02/03/04/06).
 *
 * 게임산업법 2024-03 개정에 따라 L4 compliance 게임은 확률을 공시해야 한다.
 * 본 util 은 PIXI.Text 를 stage 에 배치해 사용자에게 **항상 보이는** 확률 공시를
 * 제공한다. disclosure 공란이면 즉시 throw — meta.probabilityDisclosure 를
 * 누락한 game 을 runtime 에서 탐지.
 *
 * 위치:
 *   - top    : stage 상단 16,16 (기본)
 *   - bottom : stage 하단 16, (height - 40)
 *
 * 애니메이션:
 *   - reducedMotion=false → 300ms fade-in (alpha 0 → 1).
 *   - reducedMotion=true  → 즉시 alpha=1 (DESIGN §10.4 prefers-reduced-motion).
 */
import type { Container } from 'pixi.js';
import { Text, TextStyle, Ticker } from 'pixi.js';

export interface DisclosureRenderOptions {
  readonly reducedMotion: boolean;
  readonly position?: 'top' | 'bottom';
  /** stage height — bottom 위치 계산 시 사용. 기본 600. */
  readonly stageHeight?: number;
  /** stage width — text wrap 폭. 기본 800. */
  readonly stageWidth?: number;
}

/** 공시 Text 스타일 — 작고 읽기 쉬운 흰색 + 검정 테두리. */
function buildStyle(width: number): TextStyle {
  return new TextStyle({
    fontFamily: 'system-ui, sans-serif',
    fontSize: 14,
    fill: 0xffffff,
    stroke: { color: 0x000000, width: 2 },
    wordWrap: true,
    wordWrapWidth: Math.max(200, width - 32),
  });
}

/**
 * 확률 공시 렌더. stage 에 PIXI.Text 를 addChild 하고 fade-in.
 *
 * @throws Error — disclosure 가 공란/null/undefined 인 경우 (GAME-06 위반).
 */
export function renderDisclosure(
  stage: Container,
  disclosure: string,
  opts: DisclosureRenderOptions,
): Text {
  if (typeof disclosure !== 'string' || disclosure.trim().length === 0) {
    throw new Error('확률 공시 누락 — GAME-06');
  }
  const width = opts.stageWidth ?? 800;
  const height = opts.stageHeight ?? 600;
  const text = new Text({
    text: disclosure,
    style: buildStyle(width),
  });
  text.x = 16;
  text.y = opts.position === 'bottom' ? height - 40 : 16;
  text.alpha = opts.reducedMotion ? 1 : 0;
  stage.addChild(text);

  if (!opts.reducedMotion) {
    // 300ms fade-in via Pixi shared ticker.
    const start = performance.now();
    const duration = 300;
    const ticker = Ticker.shared;
    const tick = (): void => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / duration);
      text.alpha = t;
      if (t >= 1) {
        ticker.remove(tick);
      }
    };
    ticker.add(tick);
  }
  return text;
}

/**
 * 룰렛 prize 배열 → 공시 문자열 빌더.
 * 예: "꽝: 50.00% / 굿즈: 30.00% / 레어: 20.00%"
 */
export function buildPrizesDisclosure(
  prizes: readonly { name: string; probability: number }[],
): string {
  return prizes
    .map((p) => `${p.name}: ${(p.probability * 100).toFixed(2)}%`)
    .join(' / ');
}
